import {
  Button,
  Description,
  Input as HeroUiInput,
  Modal,
  Spinner,
  TextArea as HeroUiTextArea,
  toast,
} from '@heroui/react';
import * as React from 'react';
import type { ChangeEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { apiClient } from '@hs/shared';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

/** HeroUI `Input`/`TextArea`（RAC）类型偏紧，薄封装承接表单 register 等用法 */
function Input(props: Record<string, unknown>) {
  return <HeroUiInput {...props} />;
}
function TextArea(props: Record<string, unknown>) {
  return <HeroUiTextArea {...props} />;
}
/**
 * 表单 Schema
 */
const prizeSchema = z.object({
  level: z.string().min(1, '奖品等级不能为空').max(100, '奖品等级不能超过100个字符'),
  name: z.string().min(1, '奖品名称不能为空').max(200, '奖品名称不能超过200个字符'),
  model: z
    .string()
    .max(200, '奖品型号不能超过200个字符')
    .optional()
    .nullable()
    .transform((val) => (val === '' || !val ? null : val)),
  value: z
    .string()
    .optional()
    .nullable()
    .transform((val) => {
      if (!val || val === '') return null;
      const num = parseFloat(val);
      return isNaN(num) ? null : num;
    }),
  imageUrl: z
    .string()
    .max(500, '奖品图片URL不能超过500个字符')
    .optional()
    .nullable()
    .transform((val) => (val === '' || !val ? null : val)),
  description: z
    .string()
    .max(1000, '介绍不能超过1000个字符')
    .optional()
    .nullable()
    .transform((val) => (val === '' || !val ? null : val)),
  quantity: z
    .string()
    .optional()
    .nullable()
    .transform((val) => {
      if (!val || val === '') return null;
      const num = parseInt(val, 10);
      return isNaN(num) ? null : num;
    }),
});

type PrizeFormInput = z.input<typeof prizeSchema>;
type PrizeFormOutput = z.output<typeof prizeSchema>;

/**
 * 对话框模式
 */
export type PrizeEditorMode = 'create' | 'edit';

/**
 * Prize Editor Dialog Props
 */
export interface PrizeEditorDialogProps {
  open: boolean;
  mode?: PrizeEditorMode;
  prizeData?: any; // 编辑模式时的奖品数据
  activityId: string; // 活动ID
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

/**
 * 奖品编辑对话框组件
 */
export function PrizeEditorDialog({
  open,
  mode = 'create',
  prizeData,
  activityId,
  onOpenChange,
  onSuccess,
}: PrizeEditorDialogProps) {
  const utils = trpc.useUtils();
  const isEdit = mode === 'edit';

  const form = useForm<PrizeFormInput, unknown, PrizeFormOutput>({
    resolver: zodResolver(prizeSchema) as any,
    defaultValues: {
      level: '',
      name: '',
      model: null,
      value: '',
      imageUrl: null,
      description: null,
      quantity: '',
    },
  });

  /** 奖品图片对应的资源 ID，用于删除时调用 assets.delete（仅本地上传的才有） */
  const [prizeImageMediaId, setPrizeImageMediaId] = React.useState<string | null>(null);
  const [imageUploading, setImageUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // 删除媒体（表与云存储同时删除）
  const deleteMediaMutation = trpc.assets.delete.useMutation({
    onError: () => toast.danger('删除图片失败'),
  });

  // 创建奖品
  const createPrizeMutation = trpc.activityPrizes.create.useMutation({
    onSuccess: () => {
      toast.success('奖品创建成功');
      form.reset();
      onOpenChange(false);
      onSuccess();
      utils.activityPrizes.list.invalidate();
    },
  });

  // 更新奖品
  const updatePrizeMutation = trpc.activityPrizes.update.useMutation({
    onSuccess: () => {
      toast.success('奖品更新成功');
      form.reset();
      onOpenChange(false);
      onSuccess();
      utils.activityPrizes.list.invalidate();
    },
  });

  // 初始化表单数据
  React.useEffect(() => {
    if (!open) {
      form.reset();
      return;
    }

    if (isEdit && prizeData) {
      // 编辑模式：填充现有数据
      form.reset({
        level: prizeData.level || '',
        name: prizeData.name || '',
        model: prizeData.model || null,
        value: prizeData.value != null ? String(prizeData.value) : '',
        imageUrl: prizeData.imageUrl || null,
        description: prizeData.description || null,
        quantity: prizeData.quantity != null ? String(prizeData.quantity) : '',
      });
    } else {
      // 创建模式：使用默认值
      form.reset({
        level: '',
        name: '',
        model: null,
        value: '',
        imageUrl: null,
        description: null,
        quantity: '',
      });
    }
    setPrizeImageMediaId(null);
  }, [open, mode, prizeData, isEdit, form]);

  const onSubmit = async (values: PrizeFormOutput) => {
    try {
      // 转换表单数据为 API 需要的格式
      const submitData = {
        activityId: activityId,
        level: values.level,
        name: values.name,
        model: values.model,
        value: values.value,
        imageUrl: values.imageUrl,
        description: values.description,
        quantity: values.quantity,
      };

      if (isEdit && prizeData) {
        await updatePrizeMutation.mutateAsync({
          id: prizeData.id,
          data: submitData,
        });
      } else {
        await createPrizeMutation.mutateAsync(submitData);
      }
    } catch (error) {
      console.error('提交奖品失败:', error);
    }
  };

  const dialogTitle = React.useMemo(() => {
    return isEdit ? '编辑奖品' : '新建奖品';
  }, [isEdit]);

  const submitButtonText = React.useMemo(() => {
    return isEdit ? '保存' : '确定';
  }, [isEdit]);

  const isSubmitting = createPrizeMutation.isPending || updatePrizeMutation.isPending;
  const errors = form.formState.errors;

  const imageUrl = form.watch('imageUrl');

  const handlePrizeImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || isSubmitting) return;
    setImageUploading(true);
    try {
      const res = await apiClient.uploadFile({
        file,
        onProgress: () => {},
      });
      if (res.url) form.setValue('imageUrl', res.url);
      if (res.id) setPrizeImageMediaId(res.id);
    } catch {
      toast.danger('上传失败');
    } finally {
      setImageUploading(false);
    }
  };

  const handleRemovePrizeImage = async () => {
    if (prizeImageMediaId) {
      try {
        await deleteMediaMutation.mutateAsync({ id: prizeImageMediaId });
      } catch {
        return;
      }
    }
    setPrizeImageMediaId(null);
    form.setValue('imageUrl', null);
  };

  return (
    <Modal isOpen={open} onOpenChange={onOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog className="max-h-[90vh] max-w-2xl">
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">{dialogTitle}</Modal.Heading>
              <Description className="text-sm">填写奖品信息并保存</Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-4">
              <form
                id="prize-editor-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-h-0 flex-col gap-4"
              >
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    奖品等级 <span className="text-danger">*</span>
                  </label>
                  <Input
                    placeholder="请输入奖品等级（如：一等奖、二等奖等）"
                    {...form.register('level')}
                    isDisabled={isSubmitting}
                    maxLength={100}
                  />
                  {errors.level && <p className="text-danger text-sm">{errors.level.message}</p>}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    奖品名称 <span className="text-danger">*</span>
                  </label>
                  <Input
                    placeholder="请输入奖品名称（如：iPhone 15 Pro、100元话费等）"
                    {...form.register('name')}
                    isDisabled={isSubmitting}
                    maxLength={200}
                  />
                  {errors.name && <p className="text-danger text-sm">{errors.name.message}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">奖品型号</label>
                    <Controller
                      control={form.control}
                      name="model"
                      render={({ field }) => (
                        <Input
                          placeholder="请输入奖品型号（如：256GB）"
                          value={field.value || ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            field.onChange(e.target.value || null)
                          }
                          isDisabled={isSubmitting}
                          maxLength={200}
                        />
                      )}
                    />
                    {errors.model && <p className="text-danger text-sm">{errors.model.message as string}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">奖品价值（元）</label>
                    <Controller
                      control={form.control}
                      name="value"
                      render={({ field }) => (
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="请输入奖品价值"
                          value={field.value || ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            field.onChange(e.target.value || null)
                          }
                          isDisabled={isSubmitting}
                        />
                      )}
                    />
                    {errors.value && <p className="text-danger text-sm">{errors.value.message as string}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">奖品图片</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isSubmitting || imageUploading}
                    onChange={handlePrizeImageChange}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      isDisabled={isSubmitting || imageUploading}
                      onPress={() => fileInputRef.current?.click()}
                    >
                      {imageUploading ? '上传中…' : '选择图片'}
                    </Button>
                    {imageUrl ? (
                      <Button
                        type="button"
                        variant="ghost"
                        isDisabled={isSubmitting || deleteMediaMutation.isPending}
                        onPress={() => void handleRemovePrizeImage()}
                      >
                        移除
                      </Button>
                    ) : null}
                  </div>
                  {imageUrl ? (
                    <button
                      type="button"
                      className="relative mt-2 block max-w-xs overflow-hidden rounded-lg border text-left"
                      onClick={() => window.open(imageUrl, '_blank', 'noopener,noreferrer')}
                    >
                      <img src={imageUrl} alt="奖品预览" className="max-h-40 w-full object-contain" />
                    </button>
                  ) : null}
                  {errors.imageUrl && <p className="text-danger text-sm">{errors.imageUrl.message as string}</p>}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">介绍</label>
                  <Controller
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                    <TextArea
                      placeholder="请输入奖品介绍"
                      value={field.value || ''}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                        field.onChange(e.target.value || null)
                      }
                      isDisabled={isSubmitting}
                      maxLength={1000}
                      rows={3}
                      className="resize-none"
                    />
                    )}
                  />
                  {errors.description && (
                    <p className="text-danger text-sm">{errors.description.message as string}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">数量</label>
                  <Controller
                    control={form.control}
                    name="quantity"
                    render={({ field }) => (
                      <Input
                        type="number"
                        placeholder="奖品份数（可选）"
                        value={field.value || ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          field.onChange(e.target.value || null)
                        }
                        isDisabled={isSubmitting}
                        min={1}
                      />
                    )}
                  />
                  {errors.quantity && (
                    <p className="text-danger text-sm">{errors.quantity.message as string}</p>
                  )}
                </div>
              </form>
            </Modal.Body>
            <Modal.Footer className="border-t">
              <Button variant="ghost" isDisabled={isSubmitting} onPress={() => onOpenChange(false)}>
                取消
              </Button>
              <Button
                variant="primary"
                isDisabled={isSubmitting}
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isSubmitting ? <Spinner className="mr-2 size-4" /> : null}
                {submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
